import React, { useCallback, useMemo, useRef, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { GET_STAFF_MANAGEMENT_EVENTS } from "graphql/query/staff-management-gql";
import StaffManagement from "components/staff-management";
import useStaffEventWorkerCounts from "components/staff-management/hooks/use-staff-event-worker-counts";
import { PageWrapDefault, Tables } from "components/layout";
import { Localize } from "components/service";
import { useActiveMenuItem, useBreadCrumbs } from "components/use-hooks";


const basePath = StaffManagement.Const.basePath;
const tableHelper = StaffManagement.Helpers.TableMain;
const PAGE_SIZE = 25;
const EMPTY_EVENTS = [];
const getCountsContextKey = ({ page, text, computedStatus, where }) => JSON.stringify({
    page, text, computedStatus, where,
});

const StaffManagementPage = () => {
    useActiveMenuItem([ "staff-management" ], [ "staff-management" ]);

    const history = useHistory();
    const match = useRouteMatch(`${ basePath }/page/:pageNum`);
    const requestedPage = match ? Number(match.params.pageNum) || 1 : 1;
    const [ searchText, setSearchText ] = useState("");
    const [ lifecycleStatus, setLifecycleStatus ] = useState();
    const [ constructionPeriod, setConstructionPeriod ] = useState([]);
    const [ exhibitionPeriod, setExhibitionPeriod ] = useState([]);
    const [ dismantlingPeriod, setDismantlingPeriod ] = useState([]);
    const [ visibleSnapshot, setVisibleSnapshot ] = useState(null);
    const tableVariables = useRef({});
    const onTableVariables = useCallback((variables) => { tableVariables.current = variables; }, []);
    const onEventsData = useCallback((events) => {
        setVisibleSnapshot({ events, contextKey: getCountsContextKey(tableVariables.current) });
    }, []);

    useBreadCrumbs([ {
        label: Localize({ children: "BREADCRUMBS.Label_StaffManagement" }).props.children,
        path: basePath,
    } ]);

    const resetPage = useCallback(() => {
        if (requestedPage !== 1)
        {
            history.push(basePath);
        }
    }, [ history, requestedPage ]);

    const computedStatus = StaffManagement.Helpers.EventsApi.getComputedStatusVariable(
        lifecycleStatus
    );
    const eventWhere = useMemo(() => StaffManagement.Helpers.EventsApi.buildEventsWhere({
        constructionPeriod,
        exhibitionPeriod,
        dismantlingPeriod,
    }), [ constructionPeriod, exhibitionPeriod, dismantlingPeriod ]);
    const countsContextKey = getCountsContextKey({
        page: requestedPage, text: searchText, computedStatus, where: eventWhere,
    });
    const workerCounts = useStaffEventWorkerCounts({
        events: visibleSnapshot?.contextKey === countsContextKey ? visibleSnapshot.events : EMPTY_EVENTS,
        contextKey: countsContextKey,
    });

    return (
        <PageWrapDefault
            dataExist={ true }
            title={ <Localize>PAGES.Title_StaffManagement</Localize> }>
            <Tables.Main
                model="exhibitionEvents"
                query={ GET_STAFF_MANAGEMENT_EVENTS }
                routeUrl={ basePath }
                currentPage={ requestedPage }
                searchText={ searchText }
                objFilter={ eventWhere }
                setVariables={ onTableVariables }
                onData={ onEventsData }
                currentObj={ { workerCountsByEventID: workerCounts.byEventID, retryWorkerCounts: workerCounts.retry } }
                varSet={ {
                    perPage: PAGE_SIZE,
                    computedStatus,
                } }
                tableHelper={ tableHelper }
                extraClass="staff-management-list__table"
                scroll={ { x: 1607 } }>
                <StaffManagement.Filters.Table
                    searchText={ searchText }
                    setSearchText={ setSearchText }
                    lifecycleStatus={ lifecycleStatus }
                    setLifecycleStatus={ setLifecycleStatus }
                    constructionPeriod={ constructionPeriod }
                    setConstructionPeriod={ setConstructionPeriod }
                    exhibitionPeriod={ exhibitionPeriod }
                    setExhibitionPeriod={ setExhibitionPeriod }
                    dismantlingPeriod={ dismantlingPeriod }
                    setDismantlingPeriod={ setDismantlingPeriod }
                    resetPage={ resetPage } />
            </Tables.Main>
        </PageWrapDefault>
    );
};

export default StaffManagementPage;
